import { useState, useCallback } from 'react';
import { useDebouncedSearch } from './useDebouncedSearch';

interface UsePaginatedDataOptions {
  pageSize?: number;
  searchDelay?: number;
  initialPage?: number;
}

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

interface UsePaginatedDataReturn {
  pagination: PaginationState;
  searchTerm: string;
  debouncedSearch: string;
  isSearching: boolean;
  setSearchTerm: (value: string) => void;
  setPage: (page: number) => void;
  nextPage: (maxPage: number) => void;
  prevPage: () => void;
  resetToFirstPage: () => void;
}

export const usePaginatedData = ({
  pageSize = 10,
  searchDelay = 500,
  initialPage = 1,
}: UsePaginatedDataOptions = {}): UsePaginatedDataReturn => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPage,
    pageSize,
  });

  const { 
    searchTerm, 
    debouncedSearchTerm, 
    setSearchTerm: setSearchTermInternal, 
    isSearching
  } = useDebouncedSearch({
    delay: searchDelay,
    initialValue: '',
  });

  // Custom setSearchTerm that also resets pagination
  const setSearchTerm = useCallback((value: string) => {
    setSearchTermInternal(value);
    setPagination((prev) => ({ ...prev, pageIndex: 1 }));
  }, [setSearchTermInternal]);

  const setPage = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, pageIndex: page }));
  }, []);

  const nextPage = useCallback((maxPage: number) => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: Math.min(maxPage, prev.pageIndex + 1),
    }));
  }, []);

  const prevPage = useCallback(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: Math.max(1, prev.pageIndex - 1),
    }));
  }, []);

  const resetToFirstPage = useCallback(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 1 }));
  }, []);

  return {
    pagination,
    searchTerm,
    debouncedSearch: debouncedSearchTerm,
    isSearching,
    setSearchTerm,
    setPage,
    nextPage,
    prevPage,
    resetToFirstPage,
  };
};

